// src/hooks/useDraggable.ts
import { useState, useEffect, RefObject, useCallback, useLayoutEffect } from 'react';
import { TWindowPosition } from '../types';

interface DragOffset {
  x: number;
  y: number;
}

interface UseDraggableProps {
  initialPosition: TWindowPosition;
  containerRef: RefObject<HTMLElement | null>;
  elementRef: RefObject<HTMLDivElement | null>;
  onPositionChange: (position: TWindowPosition) => void;
  disabled?: boolean;
  autoAlignToMiddle?: boolean,
}

const useDraggable = ({
  initialPosition,
  containerRef,
  elementRef,
  onPositionChange,
  disabled = false,
  autoAlignToMiddle = false,
}: UseDraggableProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent): void => {
    if (disabled) return;


    // Prevent dragging when clicking on specific elements (like controls)
    if ((e.target as HTMLElement).closest('.window-controls')) return;

    e.stopPropagation();
    e.preventDefault();

    setIsDragging(true);

    if (elementRef.current) {
      const elementRect = elementRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - elementRect.left,
        y: e.clientY - elementRect.top,
      });
    }
  }, [disabled, elementRef])

  const handleMouseMove = useCallback((e: MouseEvent): void => {
    if (!isDragging || !containerRef.current || !elementRef.current) return;

    e.stopPropagation();
    e.preventDefault();

    const containerRect = containerRef.current.getBoundingClientRect();
    const elementRect = elementRef.current.getBoundingClientRect();

    // Calculate new position within container bounds
    let newX = e.clientX - containerRect.left - dragOffset.x;
    let newY = e.clientY - containerRect.top - dragOffset.y;

    // Ensure element stays within container bounds
    newX = Math.max(0, Math.min(newX, containerRect.width - elementRect.width));
    newY = Math.max(0, Math.min(newY, containerRect.height - elementRect.height));

    onPositionChange({ x: newX, y: newY });
  }, [containerRef, dragOffset.x, dragOffset.y, elementRef, isDragging, onPositionChange])

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, handleMouseMove]);

  useEffect(() => {
    if (!autoAlignToMiddle) return;
    if (!containerRef.current || !elementRef.current) return;
    const { width: maxWidth, height: maxHeight } = containerRef.current.getBoundingClientRect();
    const { width, height } = elementRef.current.getBoundingClientRect();

    onPositionChange({
      x: maxWidth > width ? (maxWidth - width) / 2 : 0,
      y: maxHeight > height ? (maxHeight - height) / 2 : 0,
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAlignToMiddle]); // leave it like this. I want this effect to only trigger when the component is mounted to force position its element to the middle of the screen (autoAlignToMiddle dep is in case the component is reused by React)

  return {
    isDragging,
    handleMouseDown,
  };
};

export default useDraggable;
